import React, { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useGetRealEstateDetails } from "../../helperHooks/getDetails";
import defaultImage from "src/components/image/Image_not_available.png";

const RealEstate = () => {
  const history = useHistory();
  const { data: properties, loading, error } = useGetRealEstateDetails();
  const baseImageBucketUrl = process.env.REACT_APP_S3_IMAGE_BUCKET_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: "70vh", backgroundColor: "#f6f4ef" }}>
      <Box
        sx={{
          px: { xs: 3, md: 8 },
          pt: { xs: 10, md: 15 },
          pb: { xs: 7, md: 10 },
          textAlign: "center",
          backgroundColor: "#eeece5",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Typography
            component="p"
            sx={{
              mb: 2,
              color: "#9a794b",
              fontSize: "0.68rem",
              fontWeight: 600,
              letterSpacing: "0.2em",
            }}
          >
            THOUGHTFULLY CREATED
          </Typography>
          <Typography
            component="h1"
            variant="h1"
            sx={{
              fontSize: { xs: "2.8rem", md: "4.8rem" },
              lineHeight: 1.08,
            }}
          >
            Places to call
            <br />
            your own
          </Typography>
          <Typography
            sx={{
              maxWidth: 680,
              mx: "auto",
              mt: 2.5,
              color: "text.secondary",
              fontSize: { xs: "0.92rem", md: "1rem" },
              lineHeight: 1.8,
            }}
          >
            Discover considered living spaces that bring modern comfort,
            enduring design, and a genuine sense of belonging together.
          </Typography>
        </motion.div>
      </Box>

      <Box sx={{ maxWidth: 1360, mx: "auto", px: { xs: 2.5, md: 6 }, py: { xs: 6, md: 9 } }}>
        {loading ? (
          <Typography textAlign="center" color="text.secondary">
            Loading properties…
          </Typography>
        ) : error ? (
          <Typography textAlign="center" color="error">
            We couldn&apos;t load properties. Please try again later.
          </Typography>
        ) : !properties?.length ? (
          <Typography textAlign="center" color="text.secondary">
            New developments will be shown here soon.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              columnGap: { xs: 2.5, md: 3.5 },
              rowGap: { xs: 3, md: 4 },
            }}
          >
            {properties.map((property, index) => (
              <motion.article
                key={property.id || `${property.projectTitle}-${index}`}
                style={{
                  gridColumn: properties.length === 1 ? "1 / -1" : undefined,
                  justifySelf: properties.length === 1 ? "center" : undefined,
                  width: "100%",
                  maxWidth: properties.length === 1 ? 760 : "none",
                }}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.65,
                  ease: [0.22, 1, 0.36, 1],
                  delay: Math.min((index % 3) * 0.09, 0.18),
                }}
                whileHover={{ y: -5 }}
              >
                <Box
                  sx={{
                    overflow: "hidden",
                    height: "100%",
                    backgroundColor: "#fffefa",
                    border: "1px solid rgba(41,39,32,0.1)",
                    boxShadow: "0 8px 28px rgba(41,39,32,0.045)",
                    transition: "box-shadow 350ms ease, border-color 350ms ease",
                    "&:hover": {
                      borderColor: "rgba(154,121,75,0.42)",
                      boxShadow: "0 18px 42px rgba(41,39,32,0.12)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", aspectRatio: "1.45 / 1", overflow: "hidden", backgroundColor: "#dedbd2" }}>
                    <Box
                      component="img"
                      className="estate-card__image"
                      src={
                        property.projectImage
                          ? `${baseImageBucketUrl}${property.projectImage}`
                          : defaultImage
                      }
                      alt={property.projectTitle}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 850ms cubic-bezier(0.22, 1, 0.36, 1)",
                        ".MuiBox-root:hover &": { transform: "scale(1.045)" },
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(0deg, rgba(25,24,21,0.22), transparent 52%)",
                        pointerEvents: "none",
                      }}
                    />
                    {property.status && (
                      <Typography
                        sx={{
                          position: "absolute",
                          top: 18,
                          left: 18,
                          px: 1.5,
                          py: 0.8,
                          color: "#292720",
                          backgroundColor: "rgba(246,244,239,0.94)",
                          fontSize: "0.62rem",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                        }}
                      >
                        {property.status}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 184,
                      px: { xs: 2.2, md: 2.6 },
                      pt: 2.4,
                      pb: 2.2,
                    }}
                  >
                    <Typography
                      sx={{
                        mb: 0.9,
                        color: "#9a794b",
                        fontSize: "0.62rem",
                        fontWeight: 600,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                      }}
                    >
                      HOMELY RESIDENCES
                    </Typography>
                    <Typography
                      component="h2"
                      sx={{
                        color: "#292720",
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontSize: { xs: "1.55rem", md: "1.8rem" },
                        lineHeight: 1.18,
                      }}
                    >
                      {property.projectTitle}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        columnGap: 2.5,
                        rowGap: 0.8,
                        mt: 1.7,
                        color: "#716e65",
                      }}
                    >
                      {property.location && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.65 }}>
                          <LocationOnOutlinedIcon sx={{ fontSize: 16, color: "#9a794b" }} />
                          <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.5 }}>
                            {property.location}
                          </Typography>
                        </Box>
                      )}
                      {property.area && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.65 }}>
                          <StraightenOutlinedIcon sx={{ fontSize: 15, color: "#9a794b" }} />
                          <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.5 }}>
                            {property.area}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mt: "auto",
                        pt: 2,
                        borderTop: "1px solid rgba(41,39,32,0.12)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#716e65",
                          fontSize: "0.66rem",
                          fontWeight: 600,
                          letterSpacing: "0.11em",
                          textTransform: "uppercase",
                        }}
                      >
                        Discover the development
                      </Typography>
                      {property.displayDetailsPageFlag && (
                        <Button
                          onClick={() =>
                            history.push(`/real-estate/${property.id}`, {
                              property,
                              loading,
                            })
                          }
                          aria-label={`Explore ${property.projectTitle}`}
                          sx={{
                            minWidth: 42,
                            width: 42,
                            height: 42,
                            p: 0,
                            flexShrink: 0,
                            borderRadius: "50%",
                            color: "#f6f4ef",
                            backgroundColor: "#292720",
                            transition: "background-color 250ms ease, transform 250ms ease",
                            "&:hover": {
                              backgroundColor: "#9a794b",
                              transform: "translateX(2px)",
                            },
                          }}
                        >
                          <ArrowForwardIcon fontSize="small" />
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </motion.article>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RealEstate;
